/**
 * Group calendar events into day buckets for the custom mobile Agenda.
 * Pure: no DOM, no date "now" — buckets are keyed by the event's LOCAL calendar
 * day (what the user sees), each bucket sorted by time ascending, buckets sorted
 * chronologically. The component formats the day label; this only structures.
 */
export interface HasDueDate {
  dueDate?: Date | string | number | null;
}

export interface AgendaDayGroup<T extends HasDueDate> {
  /** YYYY-MM-DD local-day key */
  dateKey: string;
  /** the day at local midnight, for label formatting in the component */
  date: Date;
  items: T[];
}

function localDayKey(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

export function groupEventsByDay<T extends HasDueDate>(events: T[]): AgendaDayGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const e of events || []) {
    if (e?.dueDate === undefined || e?.dueDate === null) continue;
    const d = new Date(e.dueDate);
    if (isNaN(d.getTime())) continue;
    const key = localDayKey(d);
    const bucket = groups.get(key);
    if (bucket) bucket.push(e);
    else groups.set(key, [e]);
  }

  const out: AgendaDayGroup<T>[] = [];
  for (const [dateKey, items] of groups.entries()) {
    items.sort((a, b) => new Date(a.dueDate as any).getTime() - new Date(b.dueDate as any).getTime());
    const [y, m, day] = dateKey.split('-').map(Number);
    out.push({ dateKey, date: new Date(y, m - 1, day), items });
  }
  out.sort((a, b) => (a.dateKey < b.dateKey ? -1 : a.dateKey > b.dateKey ? 1 : 0));
  return out;
}
